import * as Sentry from '@sentry/react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Icon,
  SR_ICONS,
} from '@sr/ui-library';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useCurrentUserAuth } from '../hooks/useCurrentUserAuth';
import { globalService } from '../reducers/globalState/globalStateSlice';
import { useTypedSelector } from '../stateStore';

export interface IErrorPageProps {
  details?: {
    correlationId?: string;
    path?: string;
    requiredPermissions?: string[];
    workspaceId?: string;
  };
  genericError?: boolean;
  noInternet?: boolean;
  notAuthorized?: boolean;
  notFound?: boolean;
  /** Soft retry instead of a full page reload. Defaults to `window.location.reload`. */
  onRetry?: () => void;
}

export const ErrorPage = ({
  details,
  genericError,
  noInternet,
  notAuthorized,
  notFound,
  onRetry,
}: IErrorPageProps) => {
  const currentError = useTypedSelector(globalService.selectors.currentError);
  const networkError = noInternet || currentError?.code === 'ERR_NETWORK';
  const location = useLocation();

  const { user: authUser } = useAuth();
  const { currentUser } = useCurrentUserAuth();

  const [detailsCopied, setDetailsCopied] = useState(false);

  const handleRefreshClick = onRetry ?? (() => window.location.reload());

  // Generate a short correlation/support code for easier support lookup
  const supportCode = useMemo(() => {
    if (!notAuthorized) return undefined;

    try {
      const base = JSON.stringify({
        p: details?.path ?? location.pathname + location.search,
        req: details?.requiredPermissions,
        t: Date.now(),
        uid: currentUser?._id,
        wp: details?.workspaceId,
      });

      let h = 0;

      for (let i = 0; i < base.length; i++)
        h = ((h << 5) - h + base.charCodeAt(i)) | 0;

      return Math.abs(h).toString(36).slice(0, 8);
    } catch {
      return undefined;
    }
  }, [notAuthorized, details, location, currentUser]);

  // Telemetry for generic error views
  useEffect(() => {
    if (!notAuthorized) return;

    const payload = {
      correlationId: details?.correlationId ?? supportCode,
      // Sentry scrubs "auth" in payloads, but the sub (subject id) is not sensitive, so just remove the auth0| prefix
      loginId: authUser?.sub?.replace('auth0|', ''),
      path: details?.path ?? location.pathname + location.search,
      requiredPermissions: details?.requiredPermissions ?? [],
      userId: currentUser?._id,
      workspaceId: details?.workspaceId,
    };

    try {
      Sentry.captureMessage('Authorization denied', {
        extra: payload,
        level: 'warning',
      });
      // If you have an analytics library, you can emit here as well.
      // analytics?.track?.('authz_denied', payload);
    } catch {
      // no-op
    }
  }, [notAuthorized, details, location, authUser, currentUser, supportCode]);

  if (genericError || notAuthorized || notFound) {
    const messageText = notAuthorized
      ? 'You do not have access to the page you requested.'
      : notFound
        ? 'We cannot find the page you are looking for.'
        : 'Something went wrong.';

    const icon = notAuthorized
      ? SR_ICONS.prohibit
      : notFound
        ? SR_ICONS.question
        : SR_ICONS.smileySad;

    const handleCopyDetailsClick = async () => {
      try {
        const report = [
          `Diagnostic details`,
          `Message: ${messageText}`,
          `Path: ${details?.path ?? location.pathname + location.search}`,
          `Workspace: ${details?.workspaceId ?? 'n/a'}`,
          `Required permissions: ${
            details?.requiredPermissions?.join(', ') || 'n/a'
          }`,
          `Login: ${authUser?.sub ?? 'n/a'}`,
          `User profile: ${currentUser?._id ?? 'n/a'} (${
            currentUser?.emailAddress ?? 'n/a'
          })`,
          `Support code: ${details?.correlationId ?? supportCode ?? 'n/a'}`,
          `Time: ${new Date().toISOString()}`,
        ].join('\n');

        await navigator.clipboard?.writeText(report);
        setDetailsCopied(true);
        window.setTimeout(() => setDetailsCopied(false), 2000);
      } catch {
        // no-op
      }
    };

    return (
      <div className="flex min-h-screen flex-col items-center gap-8 p-10 text-center">
        <Icon className="mb-5 mt-5 size-28 text-read-only-bg" icon={icon} />
        <p className="text-xl">{messageText}</p>
        <p className="text-base">
          Please try{' '}
          <Button
            className="p-0 text-base font-semibold text-sr-black underline"
            variant="link"
            onClick={handleRefreshClick}
          >
            refreshing
          </Button>{' '}
          the page, or{' '}
          <Link className="font-semibold underline" to="/">
            return to the homepage
          </Link>
          .
        </p>
        <p className="text-base">
          If the problem persists, please report it to{' '}
          <a
            className="font-semibold underline"
            href="mailto:support@screenrights.org"
          >
            support@screenrights.org
          </a>
          .
        </p>
        <Card className="mt-10 w-full max-w-2xl bg-transparent text-left shadow-none">
          <CardHeader>
            <CardTitle className="text-sm">Diagnostic details</CardTitle>
            <CardDescription className="text-xs">
              Include these details when contacting support.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-xs text-neutral-500">
            <div>
              Path:{' '}
              <span className="font-mono">
                {details?.path ?? location.pathname + location.search}
              </span>
            </div>
            {details?.workspaceId && (
              <div>
                Workspace:{' '}
                <span className="font-mono">{details.workspaceId}</span>
              </div>
            )}
            {(details?.requiredPermissions?.length ?? 0) > 0 && (
              <div>
                Required permissions:{' '}
                <span className="font-mono">
                  {details?.requiredPermissions?.join(', ')}
                </span>
              </div>
            )}
            {(authUser || currentUser) && (
              <div>
                Login:{' '}
                <span className="font-mono">{authUser?.sub ?? 'n/a'}</span>
                <br />
                User profile:{' '}
                <span className="font-mono">
                  {currentUser?._id ?? 'n/a'} (
                  {currentUser?.emailAddress ?? 'n/a'})
                </span>
              </div>
            )}
            {(details?.correlationId || supportCode) && (
              <div>
                Support code:{' '}
                <span className="font-mono">
                  {details?.correlationId ?? supportCode ?? 'n/a'}
                </span>
              </div>
            )}
          </CardContent>
          <CardFooter className="justify-end">
            <Button
              icon={detailsCopied ? <Icon icon={SR_ICONS.check} /> : undefined}
              size="sm"
              variant="outline"
              onClick={handleCopyDetailsClick}
            >
              {detailsCopied ? 'Copied' : 'Copy details'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center gap-8 p-10 text-center">
      {networkError ? (
        <Icon
          className="mb-5 size-28 text-read-only-bg"
          icon="wifiDisconnected"
        />
      ) : (
        <Icon className="mb-5 size-28 text-read-only-bg" icon="smileySad" />
      )}

      <p className="text-xl">
        {networkError
          ? 'There is no internet connection.'
          : 'There’s an error on our end. We cannot complete your request.'}
      </p>
      <p className="text-base">
        Try{' '}
        <Button
          className="btn-link-inline text-base font-semibold text-sr-black"
          variant="link"
          onClick={handleRefreshClick}
        >
          refreshing
        </Button>{' '}
        the page.
      </p>
      <p className="text-base">
        If the problem persists, please report it to{' '}
        <a
          className="font-semibold underline"
          href="mailto:support@screenrights.org"
        >
          support@screenrights.org
        </a>
        .
      </p>
    </div>
  );
};

export default ErrorPage;
